/**
 * 로그인 기관 스코프 직인 PoC (localStorage)
 * - 기관 직인: 법인 대표 직인 (구 기관등록 sealImage 이관)
 * - 회계단위 직인: 사업장·지점별 직인
 */

import { loadRawTenantsForSeal } from 'master/tenantStore';
import { getCachedAcctUnits } from 'budget/budgetAcctStore';
import {
  isBdApiEnabled,
  POC_BUCKET,
  pocParams,
  upsertPocDoc,
  enqueuePersist,
} from 'services/pocRowPersist';

export const SEAL_STORAGE_PREFIX = 'erp.seals';
export const SEAL_CHANGED_EVENT = 'erp-seal-changed';
export const SEAL_MAX_BYTES = 300 * 1024;

export const SEAL_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '사용' },
  { value: 'REVOKED', label: '폐기' },
];

function normalizeCode(compCd) {
  return String(compCd || '').trim().toUpperCase();
}

function storageKey(tenantCode) {
  return `${SEAL_STORAGE_PREFIX}|${normalizeCode(tenantCode)}`;
}

export function createSealUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `SEAL-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** @returns {{ tenantSeals: object[], unitSeals: object[] }} */
export function loadSealBundle(tenantCode) {
  const code = normalizeCode(tenantCode);
  if (!code) return { tenantSeals: [], unitSeals: [] };
  try {
    const raw = localStorage.getItem(storageKey(code));
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        tenantSeals: Array.isArray(parsed?.tenantSeals) ? parsed.tenantSeals : [],
        unitSeals: Array.isArray(parsed?.unitSeals) ? parsed.unitSeals : [],
      };
    }
  } catch {
    /* fallthrough migrate */
  }
  return migrateFromTenantRegister(code);
}

/** 기관등록에 남아 있던 sealImage → 환경설정 기관 직인으로 1회 이관 */
function migrateFromTenantRegister(code) {
  const tenants = loadRawTenantsForSeal() || [];
  const row = tenants.find((t) => normalizeCode(t.tenantCode) === code);
  const tenantSeals = [];
  if (row?.sealImage && String(row.sealImage).startsWith('data:image/')) {
    tenantSeals.push({
      sealUid: createSealUid(),
      sealNm: '대표직인',
      sealImage: row.sealImage,
      sealFileName: row.sealFileName || '',
      status: 'ACTIVE',
      defaultYn: 'Y',
      validFrom: '',
      validTo: '',
      remark: '기관등록에서 이관',
      updatedAt: new Date().toISOString(),
    });
  }
  const bundle = { tenantSeals, unitSeals: [] };
  if (tenantSeals.length) {
    saveSealBundle(code, bundle);
  }
  return bundle;
}

export function saveSealBundle(tenantCode, bundle) {
  const code = normalizeCode(tenantCode);
  if (!code) return { ok: false, message: '기관코드가 없습니다.' };
  const payload = {
    tenantSeals: Array.isArray(bundle?.tenantSeals) ? bundle.tenantSeals : [],
    unitSeals: Array.isArray(bundle?.unitSeals) ? bundle.unitSeals : [],
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(storageKey(code), JSON.stringify(payload));
  if (isBdApiEnabled()) {
    enqueuePersist(storageKey(code), () =>
      upsertPocDoc({
        bucket: POC_BUCKET.SEAL,
        rowUid: code,
        payload,
        params: pocParams({ tenantCode: code }),
      })
    );
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SEAL_CHANGED_EVENT, { detail: { tenantCode: code } }));
  }
  return { ok: true, ...payload };
}

export function loadTenantSeals(tenantCode) {
  return loadSealBundle(tenantCode).tenantSeals;
}

export function loadUnitSeals(tenantCode, acctUnitUid) {
  const uid = String(acctUnitUid || '').trim();
  return loadSealBundle(tenantCode).unitSeals.filter((s) => String(s.acctUnitUid || '') === uid);
}

/** 직인용 회계단위 목록 — 캐시(선행 loadAcctUnits) 기준 */
export function listAcctUnitsForSeal(tenantCode) {
  return (getCachedAcctUnits(tenantCode) || [])
    .filter((u) => u && u.mod !== 'D' && u.useYn !== 'N')
    .map((u) => ({
      value: u.acctUnitUid,
      label: `${u.acctUnitCode || ''} · ${u.acctUnitNm || u.acctUnitUid}`,
      code: u.acctUnitCode,
      name: u.acctUnitNm,
    }));
}

/**
 * @param {File} file
 * @returns {Promise<{ dataUrl: string, fileName: string }>}
 */
export function readSealImageFile(file) {
  return new Promise((resolve, reject) => {
    if (!file || !String(file.type || '').startsWith('image/')) {
      reject(new Error('이미지 파일만 등록할 수 있습니다.'));
      return;
    }
    if (file.size > SEAL_MAX_BYTES) {
      reject(new Error('직인 이미지는 300KB 이하로 등록하세요.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      resolve({ dataUrl: String(reader.result || ''), fileName: file.name || '' });
    reader.onerror = () => reject(new Error('파일을 읽지 못했습니다.'));
    reader.readAsDataURL(file);
  });
}

/** 기본직인 1건만 Y 유지 */
export function normalizeDefaultYn(rows, preferUid) {
  const list = (rows || []).map((r) => ({ ...r }));
  if (!list.length) return list;
  let found = false;
  list.forEach((r) => {
    if (preferUid && r.sealUid === preferUid) {
      r.defaultYn = 'Y';
      found = true;
    } else if (!preferUid && r.defaultYn === 'Y' && !found) {
      found = true;
    } else {
      r.defaultYn = 'N';
    }
  });
  if (!found) list[0].defaultYn = 'Y';
  return list;
}
